import { apiService } from './api';
import { Center, Pagination } from '../types';

export const centerService = {
  // Get all centers with pagination
  // NOTE: This is now mapped to schools API for backward compatibility.
  // The schools list endpoint now returns `{ schools, pagination }`; we
  // also tolerate the old flat-array shape in case anything still depends
  // on it. Ask for a generous limit by default — this helper is used to
  // populate dropdowns where the full set must be visible.
  getCenters: async (params?: {
    page?: number;
    limit?: number;
    search?: string;
    isActive?: boolean;
  }) => {
    try {
      const effectiveParams = { limit: 500, ...params };
      const response = await apiService.get<any>('/super-admin/schools', effectiveParams);
      const payload = response?.data;
      if (!payload) throw new Error('No data returned');
      const schools = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.schools)
          ? payload.schools
          : [];
      const pagination = (!Array.isArray(payload) && payload?.pagination) || {
        totalItems: schools.length,
        totalPages: 1,
        currentPage: 1,
        itemsPerPage: schools.length
      };
      return { centers: schools, pagination };
    } catch (error) {
      // Return empty data instead of throwing to prevent UI errors
      console.log('Failed to load centers:', error);
      return {
        centers: [],
        pagination: {
          totalItems: 0,
          totalPages: 0,
          currentPage: 1,
          itemsPerPage: params?.limit || 10
        }
      };
    }
  },

  // Get single center by ID
  getCenter: async (id: string) => {
    try {
      const response = await apiService.get<Center>(`/super-admin/schools/${id}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new center
  createCenter: async (data: { name: string; logo?: File }) => {
    if (data.logo) {
      const formData = new FormData();
      formData.append('name', data.name);
      formData.append('logo', data.logo);
      
      const response = await apiService.upload<Center>('/centers', formData);
      return response.data;
    } else {
      const response = await apiService.post<Center>('/centers', { name: data.name });
      return response.data;
    }
  },

  // Update center
  updateCenter: async (id: string, data: { name?: string; logo?: File; isActive?: boolean }) => {
    if (data.logo) {
      const formData = new FormData();
      if (data.name) formData.append('name', data.name);
      if (data.isActive !== undefined) formData.append('isActive', String(data.isActive));
      formData.append('logo', data.logo);
      
      const response = await apiService.upload<Center>(`/centers/${id}`, formData);
      return response.data;
    } else {
      const updateData: any = {};
      if (data.name) updateData.name = data.name;
      if (data.isActive !== undefined) updateData.isActive = data.isActive;
      
      const response = await apiService.put<Center>(`/centers/${id}`, updateData);
      return response.data;
    }
  },

  // Delete center
  deleteCenter: async (id: string) => {
    const response = await apiService.delete(`/centers/${id}`);
    return response.data;
  },

  // Get center statistics
  getStats: async () => {
    const response = await apiService.get<{
      total: number;
      active: number;
      inactive: number;
      totalUsers: number;
      totalAdmissions: number;
      totalStudents: number;
    }>('/centers/stats');
    return response.data;
  },
}; 